import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowUUpLeft, PencilSimple, Warning, MapPinLine, Play, Stop, MapPin } from '@phosphor-icons/react';
import TrekMap from '../components/TrekMap';
import GpsPill from '../components/GpsPill';
import OfflineBadge from '../components/OfflineBadge';
import Compass from '../components/Compass';
import ElevationChart from '../components/ElevationChart';
import { StatTile } from '../components/StatTile';
import { categoryMeta } from '../components/WaypointDialog';
import { getTrek, saveTrek, deleteTrek, getSetting } from '../lib/db';
import { GpsTracker } from '../lib/gps';
import { useHeading } from '../hooks/useHeading';
import { bearing, distance, fmtDistance, nearestOnPolyline, totalDistance } from '../lib/geo';
import { buildWalkingRoute } from '../lib/simulator';

export default function TrekDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [trek, setTrek] = useState(null);
  const [mode, setMode] = useState('view'); // view | return
  const [current, setCurrent] = useState(null);
  const [deviationThreshold, setDeviationThreshold] = useState(25);
  const trackerRef = useRef(null);
  const heading = useHeading(current?.heading);

  useEffect(() => {
    (async () => {
      const t = await getTrek(id);
      if (!t) { toast.error('Trek not found'); nav('/treks'); return; }
      setTrek(t);
      const dt = (await getSetting('deviationThreshold', 25)) || 25;
      setDeviationThreshold(dt);
    })();
    return () => trackerRef.current?.stop();
  }, [id, nav]);

  // The return route is simply the recorded route reversed.
  const returnRoute = useMemo(() => (trek ? [...trek.points].reverse() : []), [trek]);

  // Navigation guidance based on the user's current position.
  const guidance = useMemo(() => {
    if (!current || !trek || trek.points.length < 2) return null;
    const near = nearestOnPolyline(current, trek.points);
    if (!near) return null;
    const startPt = trek.points[0]; // "home"
    const distToRoute = near.distance;
    const distToStart = distance(current, startPt);
    // Target = a point along the recorded polyline ~15m closer to start than the projection.
    // We walk backwards along returnRoute to find the "next waypoint".
    // returnRoute is reversed; start index in returnRoute corresponds to (points.length-1 - segmentIndex).
    const segIdxFromEnd = trek.points.length - 1 - near.segmentIndex; // in reversed array
    const nextIdx = Math.min(returnRoute.length - 1, segIdxFromEnd + 1);
    const nextWaypoint = returnRoute[nextIdx];
    const dirBearing = bearing(current, distToRoute > deviationThreshold ? near.point : nextWaypoint);
    const distToNext = distance(current, nextWaypoint);
    const offRoute = distToRoute > deviationThreshold;
    return { distToRoute, distToStart, distToNext, dirBearing, nextWaypoint, offRoute };
  }, [current, trek, returnRoute, deviationThreshold]);

  const startReturn = async () => {
    setMode('return');
    const sim = await getSetting('simulator', false);
    const tracker = new GpsTracker({
      intervalMs: 2000,
      onPoint: (p) => setCurrent(p),
      onError: (err) => toast.error(`GPS: ${err.message || err.code}`),
    });
    trackerRef.current = tracker;
    if (sim) {
      // Simulate the return walk by playing the reversed route with a slight offset.
      const seed = returnRoute[0];
      const simRoute = returnRoute.map((p, i) => ({
        ...p,
        // Add ~5m eastward drift on the first few points so deviation warning can appear.
        lat: p.lat + (i < 5 ? 0.00005 : 0),
        lng: p.lng + (i < 5 ? 0.00005 : 0),
      }));
      tracker.startSimulator(simRoute, { speedFactor: 5 });
    } else {
      tracker.startReal();
    }
  };

  const stopReturn = () => {
    trackerRef.current?.stop();
    trackerRef.current = null;
    setMode('view');
    setCurrent(null);
  };

  const rename = async () => {
    const name = window.prompt('Rename trek', trek.name);
    if (!name) return;
    const updated = { ...trek, name };
    await saveTrek(updated);
    setTrek(updated);
    toast.success('Trek renamed');
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${trek.name}"?`)) return;
    await deleteTrek(trek.id);
    toast('Trek deleted');
    nav('/treks');
  };

  if (!trek) return <div className="p-6 text-zinc-400">Loading…</div>;

  const dist = totalDistance(trek.points);
  const dur = (trek.endedAt || Date.now()) - trek.startedAt;
  const wpColored = (trek.waypoints || []).map((w) => ({ ...w, color: categoryMeta(w.category).color }));

  if (mode === 'return') {
    return (
      <div className="min-h-[100dvh] flex flex-col">
        <header className="safe-top px-4 pt-3 pb-2 flex items-center justify-between">
          <button onClick={stopReturn} data-testid="exit-return" className="btn-tap glass-panel rounded-full p-2">
            <ArrowLeft size={18} />
          </button>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#FFD166] font-bold">Return to Start</div>
          <div className="flex items-center gap-1.5">
            <GpsPill accuracy={current?.accuracy} />
          </div>
        </header>

        {guidance?.offRoute && (
          <div data-testid="deviation-warning" className="mx-3 mt-2 rounded-xl border border-[#EF476F]/50 bg-[#EF476F]/15 px-3 py-2 flex items-start gap-2">
            <Warning size={18} weight="fill" className="text-[#EF476F] mt-0.5" />
            <div>
              <div className="text-[11px] font-black uppercase tracking-wider text-[#EF476F]">You left your recorded trail</div>
              <div className="text-xs text-rose-100/90">Approximately {fmtDistance(guidance.distToRoute)} away from your route.</div>
            </div>
          </div>
        )}

        <div className="flex-1 px-3 mt-2">
          <TrekMap points={trek.points} current={current} follow={false} waypoints={wpColored} height="100%" />
        </div>

        <div className="glass-panel mx-3 mt-3 rounded-2xl p-4">
          <div className="flex items-center gap-4">
            <Compass bearingDeg={guidance?.dirBearing} headingDeg={heading?.source === 'compass' ? heading.heading : null} size={140} />
            <div className="flex-1 space-y-2">
              <StatTile testId="return-to-next" label="To next waypoint" value={guidance ? fmtDistance(guidance.distToNext) : '—'} />
              <StatTile testId="return-to-start" label="To start" value={guidance ? fmtDistance(guidance.distToStart) : '—'} />
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest" data-testid="compass-source">
                {heading.source === 'compass' ? 'Compass available' : heading.source === 'gps' ? 'Compass unavailable — using movement direction' : 'Waiting for heading…'}
              </div>
            </div>
          </div>
        </div>
        <div className="h-4 safe-bottom" />
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] pb-24">
      <header className="safe-top px-4 pt-3 pb-2 flex items-center justify-between">
        <button onClick={() => nav('/treks')} data-testid="back-treks" className="btn-tap glass-panel rounded-full p-2">
          <ArrowLeft size={18} />
        </button>
        <OfflineBadge />
      </header>

      <div className="px-5 mt-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Trek</div>
            <h1 className="font-display font-black text-2xl truncate" data-testid="trek-title">{trek.name}</h1>
            <div className="text-[11px] text-zinc-500 mt-0.5">{new Date(trek.startedAt).toLocaleString()}</div>
          </div>
          <button onClick={rename} data-testid="rename-trek" className="btn-tap glass-panel rounded-full p-2">
            <PencilSimple size={16} />
          </button>
        </div>
      </div>

      <div className="px-3 mt-4" style={{ height: 320 }}>
        <TrekMap points={trek.points} waypoints={wpColored} height="100%" />
      </div>

      <div className="px-5 mt-4 grid grid-cols-3 gap-2">
        <StatTile testId="detail-distance" label="Distance" value={fmtDistance(dist)} />
        <StatTile testId="detail-duration" label="Duration" value={new Date(dur).toISOString().substr(11, 8)} />
        <StatTile testId="detail-points" label="Points" value={trek.points.length} />
      </div>

      <div className="px-5 mt-4">
        <ElevationChart points={trek.points} />
      </div>

      {wpColored.length > 0 && (
        <div className="px-5 mt-4" data-testid="waypoints-section">
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">Waypoints</div>
          <ul className="space-y-2">
            {wpColored.map((w) => {
              const meta = categoryMeta(w.category);
              const Icon = meta.Icon;
              return (
                <li key={w.id} data-testid={`wp-item-${w.id}`} className="glass-panel rounded-xl px-3 py-2.5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}55` }}>
                    <Icon size={16} weight="fill" color={meta.color} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-sm truncate">{w.name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono-stat">{w.lat.toFixed(5)}, {w.lng.toFixed(5)}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{meta.label}</div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="px-5 mt-6 space-y-2 text-xs text-zinc-400">
        <div className="flex items-center gap-2"><MapPinLine size={14} className="text-emerald-400" /> Start: {trek.points[0]?.lat.toFixed(5)}, {trek.points[0]?.lng.toFixed(5)}</div>
        <div className="flex items-center gap-2"><MapPinLine size={14} className="text-[#FF9F1C]" /> End: {trek.points[trek.points.length - 1]?.lat.toFixed(5)}, {trek.points[trek.points.length - 1]?.lng.toFixed(5)}</div>
      </div>

      <div className="px-5 mt-6">
        <button
          onClick={startReturn}
          data-testid="return-to-start-button"
          className="btn-tap w-full h-16 rounded-2xl bg-[#FF9F1C] text-black font-display font-black text-lg tracking-tight shadow-[0_10px_40px_-10px_rgba(255,159,28,0.75)] flex items-center justify-center gap-2 active:bg-[#FFB347]"
        >
          <ArrowUUpLeft size={22} weight="bold" /> RETURN TO START
        </button>
        <button onClick={remove} data-testid="delete-trek" className="mt-3 w-full h-11 rounded-xl bg-transparent border border-rose-500/40 text-rose-400 font-semibold text-sm active:bg-rose-500/10 btn-tap">
          Delete trek
        </button>
      </div>
    </div>
  );
}
